import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { UserContext } from '../context/UserContext';
import API from '../config/axiosConfig';
import UnsavedChangesDialog from './dialogs/UnsavedChangesDialog';
import { LeaveGuard, useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import '../pages/Settings.scss';
import './SecurityPolicySettings.scss';

// Super-admin only. Security policies inherited global -> hospital -> department,
// each level overriding the one above. Reads and writes /api/security-policies —
// see docs/Security-Policies.md.
//
// Changes stay in a local draft until "שמירת שינויים" sends them all in one
// request. Leaving with unsaved changes (another settings tab, an in-app link,
// closing the tab) asks first.

type MfaValue = 'none' | 'admins' | 'all';

type Level = {
  own: { mfa: MfaValue | null };
  effective: { mfa: MfaValue };
};

type DepartmentRow = Level & { _id: string; nameHebrew: string; nameEnglish?: string };
type HospitalRow = Level & { _id: string; nameHebrew: string; nameEnglish?: string; departments: DepartmentRow[] };

type Overview = {
  defaults: { mfa: MfaValue };
  global: Level;
  hospitals: HospitalRow[];
};

type Change = { scope: 'global' | 'hospital' | 'department'; hospital?: string; department?: string; mfa: MfaValue | null };

// Pending own values, keyed by level. Only levels whose value differs from the
// saved one are in it, so its size is the number of unsaved changes.
type Draft = Record<string, MfaValue | null>;

const GLOBAL_KEY = 'global';
const hospitalKey = (hospitalId: string) => `hospital:${hospitalId}`;
const departmentKey = (hospitalId: string, departmentId: string) => `department:${hospitalId}:${departmentId}`;

const changeOf = (key: string, mfa: MfaValue | null): Change => {
  const [scope, hospital, department] = key.split(':');
  if (scope === 'department') return { scope, hospital, department, mfa };
  if (scope === 'hospital') return { scope, hospital, mfa };
  return { scope: 'global', mfa };
};

const MFA_LABELS: Record<MfaValue, string> = {
  none: 'לא נדרש',
  admins: 'מנהלי מחלקה',
  all: 'כל המשתמשים',
};
const MFA_VALUES: MfaValue[] = ['none', 'admins', 'all'];
const STRENGTH: Record<MfaValue, number> = { none: 0, admins: 1, all: 2 };
const INHERIT = 'inherit';
const SAVED_MESSAGE_MS = 4000;

const errorMessageOf = (err: unknown, fallback: string) => {
  const apiError = err as { response?: { data?: { error?: { hebMessage?: string; message?: string } } } };
  return apiError.response?.data?.error?.hebMessage || apiError.response?.data?.error?.message || fallback;
};

type MfaCellProps = {
  own: MfaValue | null;
  inherited?: MfaValue; // the level above's effective value; absent for global
  fallback?: MfaValue; // global only: shown when nothing was ever saved
  isChanged: boolean;
  disabled: boolean;
  onChange: (value: MfaValue | null) => void;
};

const MfaCell: React.FC<MfaCellProps> = ({ own, inherited, fallback, isChanged, disabled, onChange }) => {
  const weaker = Boolean(own && inherited && STRENGTH[own] < STRENGTH[inherited]);
  return (
    <div className="policy-cell">
      <select
        className={`select-field ${own && inherited ? 'is-overridden' : ''}`}
        value={own ?? (inherited ? INHERIT : fallback)}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value === INHERIT ? null : e.target.value as MfaValue)}
      >
        {inherited && <option value={INHERIT}>ירושה ({MFA_LABELS[inherited]})</option>}
        {MFA_VALUES.map(value => <option key={value} value={value}>{MFA_LABELS[value]}</option>)}
      </select>
      {isChanged && <span className="policy-badge is-changed">לא נשמר</span>}
      {own && inherited && <span className="policy-badge">הגדרה ייעודית</span>}
      {weaker && <span className="policy-badge is-weaker">מקל לעומת הרמה שמעל</span>}
    </div>
  );
};

type Props = {
  // Lets the settings page ask before switching to another tab.
  onRegisterLeaveGuard?: (guard: LeaveGuard | null) => void;
};

const SecurityPolicySettings: React.FC<Props> = ({ onRegisterLeaveGuard }) => {
  const { user } = useContext(UserContext);
  const isSuperAdmin = Boolean(user && user.isSuperAdmin);
  const mfaEnforced = Boolean(user?.mfa?.enforced);

  const [overview, setOverview] = useState<Overview | null>(null);
  const [draft, setDraft] = useState<Draft>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [savedMessage, setSavedMessage] = useState<string>('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState<string>('');
  // Where to go once the user answers the "leave without saving?" dialog.
  const [pendingLeave, setPendingLeave] = useState<(() => void) | null>(null);

  const changeCount = Object.keys(draft).length;
  const isDirty = changeCount > 0;
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  const load = useCallback(async () => {
    try {
      const response = await API.get<Overview>('/api/security-policies', { handlingErrorLocally: true });
      setOverview(response.data);
      setError('');
    } catch (err: unknown) {
      setError(errorMessageOf(err, 'שגיאה בטעינת מדיניות האבטחה. אנא נסה שנית.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) load();
  }, [isSuperAdmin, load]);

  useEffect(() => {
    if (!savedMessage) return undefined;
    const timer = setTimeout(() => setSavedMessage(''), SAVED_MESSAGE_MS);
    return () => clearTimeout(timer);
  }, [savedMessage]);

  const askBeforeLeaving = useCallback((proceed: () => void) => {
    setPendingLeave(() => proceed);
  }, []);

  useUnsavedChangesGuard({ isDirty, onBlockedNavigation: askBeforeLeaving });

  useEffect(() => {
    if (!onRegisterLeaveGuard) return undefined;
    onRegisterLeaveGuard((proceed) => {
      if (!isDirtyRef.current) return false;
      askBeforeLeaving(proceed);
      return true;
    });
    return () => onRegisterLeaveGuard(null);
  }, [onRegisterLeaveGuard, askBeforeLeaving]);

  // A level's own value: the pending one if it was changed, else the saved one.
  const ownOf = (key: string, saved: MfaValue | null) => (key in draft ? draft[key] : saved);

  const setLevel = (key: string, saved: MfaValue | null, value: MfaValue | null) => {
    setSavedMessage('');
    setDraft(previous => {
      const next = { ...previous };
      if (value === saved) delete next[key];
      else next[key] = value;
      return next;
    });
  };

  const save = async (): Promise<boolean> => {
    if (!isDirty) return true;
    setIsSaving(true);
    setError('');
    try {
      const changes = Object.entries(draft).map(([key, mfa]) => changeOf(key, mfa));
      await API.put('/api/security-policies', { changes }, { handlingErrorLocally: true });
      setDraft({});
      setSavedMessage(changes.length === 1 ? 'השינוי נשמר.' : `${changes.length} השינויים נשמרו.`);
      await load();
      return true;
    } catch (err: unknown) {
      setError(errorMessageOf(err, 'שגיאה בשמירת המדיניות. השינויים לא נשמרו, אנא נסה שנית.'));
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const discard = () => {
    setDraft({});
    setError('');
  };

  const leaveAfter = async (saveFirst: boolean) => {
    const proceed = pendingLeave;
    if (saveFirst) {
      const saved = await save();
      // A failed save keeps the user here, with the error and the changes.
      if (!saved) {
        setPendingLeave(null);
        return;
      }
    } else {
      discard();
    }
    setPendingLeave(null);
    // proceed() navigates or switches tab from code, which the guard never stops.
    proceed?.();
  };

  const toggle = (hospitalId: string) => {
    setExpanded(previous => {
      const next = new Set(previous);
      if (next.has(hospitalId)) next.delete(hospitalId);
      else next.add(hospitalId);
      return next;
    });
  };

  const hospitals = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!overview) return [];
    if (!term) return overview.hospitals;
    return overview.hospitals.filter(hospital => [hospital.nameHebrew, hospital.nameEnglish]
      .some(name => name && name.toLowerCase().includes(term)));
  }, [overview, search]);

  if (!isSuperAdmin) {
    return null;
  }

  // Effective values follow the draft, so "ירושה (…)" and the "בפועל" column
  // show what will apply once saved.
  const globalOwn = overview ? ownOf(GLOBAL_KEY, overview.global.own.mfa) : null;
  const globalEffective: MfaValue = globalOwn ?? overview?.defaults.mfa ?? 'none';

  return (
    <div className="settings-section security-policy-settings">
      <h2 className="section-title">מדיניות אבטחה</h2>
      <p className="policy-description">
        המדיניות הכללית חלה על כל בתי החולים. בית חולים יכול לקבל הגדרה משלו, שחלה על כל מחלקותיו
        (גם על מחלקות שייפתחו בהמשך), ומחלקה יכולה לקבל הגדרה משלה. &quot;ירושה&quot; מחילה את ההגדרה של הרמה שמעל.
        משתמש השייך לכמה מחלקות נדרש לאימות דו-שלבי אם אחת מהן דורשת זאת. המדיניות חלה על כניסה עם חשבון Microsoft.
        השינויים נשמרים רק בלחיצה על &quot;שמירת שינויים&quot;.
      </p>

      {!mfaEnforced && (
        <div className="policy-notice">
          אימות דו-שלבי אינו מופעל בסביבה זו, ולכן המדיניות נשמרת אך לא נאכפת.
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {isLoading && <div className="loading">טוען...</div>}

      {overview && (
        <>
          <div className="policy-global">
            <span className="policy-global-label">מדיניות כללית — אימות דו-שלבי:</span>
            <MfaCell
              own={globalOwn}
              fallback={overview.defaults.mfa}
              isChanged={GLOBAL_KEY in draft}
              disabled={isSaving}
              onChange={(value) => value && setLevel(GLOBAL_KEY, overview.global.own.mfa, value)}
            />
          </div>

          <input
            className="input-field policy-search"
            type="search"
            placeholder="חיפוש בית חולים"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <table className="hospitals-table policy-table">
            <thead>
              <tr>
                <th>בית חולים / מחלקה</th>
                <th>אימות דו-שלבי</th>
                <th>בפועל</th>
              </tr>
            </thead>
            <tbody>
              {hospitals.map(hospital => {
                const isOpen = expanded.has(hospital._id);
                const hKey = hospitalKey(hospital._id);
                const hospitalOwn = ownOf(hKey, hospital.own.mfa);
                const hospitalEffective = hospitalOwn ?? globalEffective;
                const pendingInside = hospital.departments
                  .filter(department => departmentKey(hospital._id, department._id) in draft).length;
                return (
                  <React.Fragment key={hospital._id}>
                    <tr className={`policy-hospital-row ${hKey in draft ? 'is-changed' : ''}`}>
                      <td>
                        <button
                          type="button"
                          className="policy-expand"
                          onClick={() => toggle(hospital._id)}
                          aria-expanded={isOpen}
                          disabled={hospital.departments.length === 0}
                        >
                          {hospital.departments.length === 0 ? '•' : (isOpen ? '▾' : '◂')}
                        </button>
                        {hospital.nameHebrew}
                        <span className="policy-count">({hospital.departments.length} מחלקות)</span>
                        {!isOpen && pendingInside > 0 && (
                          <span className="policy-badge is-changed">{pendingInside} שינויים במחלקות</span>
                        )}
                      </td>
                      <td>
                        <MfaCell
                          own={hospitalOwn}
                          inherited={globalEffective}
                          isChanged={hKey in draft}
                          disabled={isSaving}
                          onChange={(value) => setLevel(hKey, hospital.own.mfa, value)}
                        />
                      </td>
                      <td>{MFA_LABELS[hospitalEffective]}</td>
                    </tr>
                    {isOpen && hospital.departments.map(department => {
                      const dKey = departmentKey(hospital._id, department._id);
                      const departmentOwn = ownOf(dKey, department.own.mfa);
                      return (
                        <tr key={department._id} className={`policy-department-row ${dKey in draft ? 'is-changed' : ''}`}>
                          <td className="policy-department-name">{department.nameHebrew}</td>
                          <td>
                            <MfaCell
                              own={departmentOwn}
                              inherited={hospitalEffective}
                              isChanged={dKey in draft}
                              disabled={isSaving}
                              onChange={(value) => setLevel(dKey, department.own.mfa, value)}
                            />
                          </td>
                          <td>{MFA_LABELS[departmentOwn ?? hospitalEffective]}</td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {hospitals.length === 0 && <div className="no-data">לא נמצאו בתי חולים</div>}

          <div className={`policy-save-bar ${isDirty ? 'is-dirty' : ''}`}>
            <span className="policy-save-status" role="status">
              {isDirty
                ? (changeCount === 1 ? 'שינוי אחד שלא נשמר' : `${changeCount} שינויים שלא נשמרו`)
                : (savedMessage || 'אין שינויים שלא נשמרו')}
            </span>
            <div className="policy-save-actions">
              <button type="button" className="button-secondary" onClick={discard} disabled={!isDirty || isSaving}>
                ביטול שינויים
              </button>
              <button type="button" className="button-primary" onClick={() => { save(); }} disabled={!isDirty || isSaving}>
                {isSaving ? 'שומר…' : 'שמירת שינויים'}
              </button>
            </div>
          </div>
        </>
      )}

      <UnsavedChangesDialog
        open={Boolean(pendingLeave)}
        isSaving={isSaving}
        onSave={() => { leaveAfter(true); }}
        onDiscard={() => { leaveAfter(false); }}
        onClose={() => setPendingLeave(null)}
      />
    </div>
  );
};

export default SecurityPolicySettings;
